import React, { useEffect, useState } from "react";
import { ShieldAlert, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";

const REASON_TONE = {
  lockout_triggered: "destructive",
  locked: "destructive",
  wrong_password: "secondary",
  unknown_email: "outline",
};

const TopIps = ({ ips, t }) => (
  <div className="flex flex-wrap gap-2" data-testid="failed-logins-top-ips">
    {ips.map((r) => (
      <span key={r.ip} className="rounded-md border border-border px-2 py-1 font-mono-jb text-xs">
        {r.ip || "—"} · {r.count}× · {r.distinct_emails} {t("failed_logins_accounts")}
      </span>
    ))}
  </div>
);

export const FailedLoginsPanel = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/failed-logins?limit=100");
    setData(r.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  return (
    <Card data-testid="failed-logins-panel">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-primary" />
            <h3 className="font-semibold">{t("failed_logins")}</h3>
          </div>
          <Button size="sm" variant="ghost" onClick={() => load()} data-testid="failed-logins-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">{t("failed_logins_desc")}</p>
        {data && (
          <>
            <div className="text-sm" data-testid="failed-logins-24h">
              {t("failed_logins_24h")}: <span className="font-mono-jb font-semibold">{data.last_24h}</span>
            </div>
            {data.top_ips.length > 0 && <TopIps ips={data.top_ips} t={t} />}
            {data.items.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="failed-logins-empty">{t("failed_logins_empty")}</p>
            ) : (
              <div className="max-h-96 overflow-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">{t("failed_logins_time")}</th>
                      <th className="px-3 py-2">{t("email")}</th>
                      <th className="px-3 py-2">IP</th>
                      <th className="px-3 py-2">{t("failed_logins_reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((e) => (
                      <tr key={e.id} className="border-t border-border" data-testid={`failed-login-row-${e.id}`}>
                        <td className="whitespace-nowrap px-3 py-2 font-mono-jb text-xs">{formatDateTime(e.created_at)}</td>
                        <td className="max-w-[220px] truncate px-3 py-2">{e.email}</td>
                        <td className="whitespace-nowrap px-3 py-2 font-mono-jb text-xs">{e.ip || "—"}</td>
                        <td className="px-3 py-2">
                          <Badge variant={REASON_TONE[e.reason] || "outline"}>{t(`reason_${e.reason}`)}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
